// src/api/axios/interceptors.ts
import { notify } from '@/shared/ui/ui-notification'
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios'

import { ENV_CONFIG } from '@/shared/config/env'
import { getAuthToken } from '@/shared/lib/storage'
import axios from 'axios'
import { ensureValidToken, isAuthError, retryWithFreshToken } from './authSession'

// Extract error message từ response envelope (object / errorMessage / message)
const extractErrorMessage = (data: any, fallbackMessage: string): string => {
  if (!data) return fallbackMessage
  if (typeof data === 'string') return data
  const msg =
    (typeof data.object === 'string' ? data.object : null) ??
    (typeof data.Object === 'string' ? data.Object : null) ??
    data.errorMessage ??
    data.ErrorMessage ??
    data.message ??
    data.Message
  return typeof msg === 'string' && msg.trim() ? msg : fallbackMessage
}

const getConnectionErrorMessage = (error: any): string => {
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
    return 'Kết nối đến máy chủ quá thời gian. Vui lòng thử lại sau.'
  }
  if (error.code === 'ERR_NETWORK' || error.message === 'Network Error' || error.request) {
    return 'Không thể kết nối đến máy chủ. Vui lòng kiểm tra kết nối mạng và thử lại.'
  }
  return error.message || 'Không thể kết nối đến máy chủ. Vui lòng thử lại sau.'
}

type AuthRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean
  _skipAuth?: boolean
  skipGlobalError?: boolean
}

declare module 'axios' {
  export interface AxiosRequestConfig {
    skipGlobalError?: boolean
  }
}

// ============ AXIOS INTERCEPTORS ============
export const setupInterceptors = (instance: AxiosInstance) => {
  // Request Interceptor - Gắn token vào header
  instance.interceptors.request.use(
    (config) => {
      if (config.data instanceof FormData) {
        config.headers['Content-Type'] = undefined
      }
      const token = getAuthToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    },
    (error) => Promise.reject(error)
  )

  // Response Interceptor - Xử lý response và refresh token
  instance.interceptors.response.use(
    async (response) => {
      // Blob response trả về nguyên vẹn
      if (response.config.responseType === 'blob') {
        return response
      }

      // ponytail: BE trả envelope không nhất quán casing (camelCase hoặc PascalCase tuỳ endpoint)
      const isError = response.data.isError
      const statusCode = Number(response.data.statusCode ?? response.data.StatusCode)
      const originalRequest = response.config as AuthRequestConfig

      // Xử lý HTTP 403 Forbidden (business-level, trong envelope)
      if (statusCode === 403) {
        if (!originalRequest.skipGlobalError) {
          notify.error('Bạn không có quyền thực hiện thao tác này.')
        }
        return Promise.reject({
          errorCode: 403,
          errorMessage: 'Forbidden',
          config: response.config,
        })
      }

      // Xử lý 401 Unauthorized — refresh + retry một lần (chống loop bằng _retry)
      if (
        isAuthError(statusCode, response.data) &&
        !originalRequest._retry &&
        !originalRequest._skipAuth &&
        !originalRequest.url?.includes('change-password')
      ) {
        return retryWithFreshToken(originalRequest, instance)
      }

      // Xử lý lỗi từ API (không phải HTTP error)
      if (isError && statusCode !== 200) {
        const errorText = extractErrorMessage(response.data, 'Lỗi từ server')
        if (statusCode !== 401 && !originalRequest.skipGlobalError) {
          notify.error(errorText)
        }
        return Promise.reject({
          errorCode: statusCode,
          errorMessage: errorText,
          message: errorText,
          config: response.config,
        })
      }

      return response.data
    },

    async (error) => {
      const originalRequest = (error.config || {}) as AuthRequestConfig

      if (axios.isCancel(error)) return Promise.reject(error)

      // Xử lý HTTP 403 Forbidden
      if (error.response?.status === 403) {
        if (!originalRequest.skipGlobalError) {
          notify.error('Bạn không có quyền thực hiện thao tác này.')
        }
        return Promise.reject(error)
      }

      // Xử lý HTTP 401 Unauthorized — refresh + retry một lần (chống loop bằng _retry)
      if (
        isAuthError(error.response?.status, error.response?.data) &&
        !originalRequest._retry &&
        !originalRequest._skipAuth &&
        !originalRequest.url?.includes('change-password')
      ) {
        return retryWithFreshToken(originalRequest, instance)
      }

      // Các lỗi HTTP khác
      let errorData = error.response?.data
      if (
        errorData instanceof Blob ||
        (errorData && typeof errorData === 'object' && typeof errorData.text === 'function')
      ) {
        try {
          const text = await errorData.text()
          errorData = JSON.parse(text)
          if (error.response) {
            error.response.data = errorData
          }
        } catch {
          // giữ nguyên nếu không parse được JSON
        }
      }

      const errorMessage = error.response
        ? extractErrorMessage(errorData, 'Lỗi từ server')
        : getConnectionErrorMessage(error)
      error.message = errorMessage
      if (!originalRequest.skipGlobalError) {
        notify.error(errorMessage)
      }
      return Promise.reject(error)
    }
  )
}

// ============ AXIOS INSTANCE ============
const instance = axios.create({
  baseURL: ENV_CONFIG.VITE_BACKEND_BASE_URL,
  timeout: 60000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
})

setupInterceptors(instance)

// Backward-compat: giữ export để không phá test/usage hiện có
export const refreshTokenAction = async (): Promise<{
  success: boolean
  accessToken?: string
}> => {
  try {
    const accessToken = await ensureValidToken()
    return { success: true, accessToken }
  } catch {
    return { success: false }
  }
}

export default instance
